/**
 * DealReferralSection - Seção "Indicação / Afiliado" da sidebar do modal de Negócio
 *
 * Exibe os dados da indicação vinculada ao deal (programa, campanha, afiliado,
 * origem e comentário do lead). Renderiza-se SOMENTE quando o deal possui
 * indicação vinculada — assim, negócios normais (sem origem em Indicação) não
 * recebem nenhuma seção extra.
 *
 * Os dados vêm da própria RPC `get_deal_by_id` (v2.4.0+), expostos em
 * `deal.referral` — sem chamada de rede adicional.
 *
 * Versão: 1.0.0
 * RELEASE: 2026-05-05
 */
import React from 'react';
import { Hash, Layers, Megaphone, UserCircle2, Link2, Handshake } from 'lucide-react';
import { CrmDeal } from '@/hooks/useCrmDeals';
import { DealQuickInfoCard } from './DealQuickInfoCard';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface DealReferralSectionProps {
  /** Bloco de indicação retornado pela RPC. NULL = nenhuma indicação. */
  referral: CrmDeal['referral'];
}

/**
 * Seção de Indicação / Afiliado (somente leitura).
 * Edição dos dados da indicação continua na tela admin de Indicações.
 */
export function DealReferralSection({ referral }: DealReferralSectionProps) {
  if (!referral) return null;

  // Iniciais para fallback do avatar do afiliado
  const initials = (referral.affiliate_name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');

  return (
    <div>
      <div className="space-y-1">
        {/* Ref. — código curto da indicação (mesmo padrão da listagem de Indicações) */}
        <DealQuickInfoCard
          icon={Hash}
          label="Ref."
          value={
            <span
              className="font-mono text-[11px] text-primary"
              title={referral.id}
            >
              {referral.id.slice(0, 8).toUpperCase()}
            </span>
          }
        />

        {/* Programa de indicação */}
        <DealQuickInfoCard
          icon={Layers}
          label="Programa"
          value={referral.program_name || '—'}
        />

        {/* Campanha — oculta quando não houver */}
        {referral.campaign_name && (
          <DealQuickInfoCard
            icon={Megaphone}
            label="Campanha"
            value={referral.campaign_name}
          />
        )}

        {/* Afiliado — avatar pequeno + nome */}
        <div className="flex items-center gap-2 py-1.5 w-full">
          <UserCircle2 className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          <span className="text-xs text-muted-foreground shrink-0">Afiliado</span>
          <div className="flex items-center gap-1.5 ml-auto min-w-0">
            <Avatar className="h-5 w-5">
              {referral.affiliate_avatar && (
                <AvatarImage
                  src={referral.affiliate_avatar}
                  alt={referral.affiliate_name || 'Afiliado'}
                />
              )}
              <AvatarFallback className="text-[9px]">
                {initials || '?'}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs font-medium truncate">
              {referral.affiliate_name || '—'}
            </span>
          </div>
        </div>

        {/* Origem da indicação — oculta quando não houver */}
        {referral.lead_source && (
          <DealQuickInfoCard
            icon={Link2}
            label="Origem"
            value={referral.lead_source}
          />
        )}
      </div>

      {/* Comentário preenchido pelo lead/afiliado no momento da indicação */}
      {referral.lead_message && (
        <div className="mt-3">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            Comentário
          </span>
          <div className="mt-1 rounded-md bg-muted/40 p-2 text-xs text-foreground whitespace-pre-wrap break-words">
            {referral.lead_message}
          </div>
        </div>
      )}
    </div>
  );
}
